import { type HobbyToolsClient, useSupabase } from '@hobbytools/data';
import type { Session, User } from '@supabase/supabase-js';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { OAuthProvider } from './config';
import { authCallbackUrl } from './urls';

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** True until the stored session has been read on first load. */
  isLoading: boolean;
  providers: OAuthProvider[];
  signIn: (email: string, password: string) => Promise<void>;
  /** Returns `needsConfirmation: true` when the user must confirm their email before signing in. */
  signUp: (input: {
    email: string;
    password: string;
    displayName: string;
    next?: string;
  }) => Promise<{ needsConfirmation: boolean }>;
  signInWithOAuth: (provider: OAuthProvider, next?: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function buildActions(supabase: HobbyToolsClient) {
  const origin = window.location.origin;
  return {
    signIn: async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    signUp: async (input: { email: string; password: string; displayName: string; next?: string }) => {
      // Checked up front so the form can say so; the sign-up trigger enforces it regardless.
      const { data: available, error: checkError } = await supabase.rpc('display_name_available', {
        p_name: input.displayName,
      });
      if (checkError) throw checkError;
      if (!available) throw Object.assign(new Error('Display name taken'), { code: 'display_name_taken' });
      const { data, error } = await supabase.auth.signUp({
        email: input.email,
        password: input.password,
        options: {
          data: { display_name: input.displayName.trim() },
          emailRedirectTo: authCallbackUrl(origin, input.next),
        },
      });
      if (error) throw error;
      return { needsConfirmation: data.session === null };
    },
    signInWithOAuth: async (provider: OAuthProvider, next?: string) => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: authCallbackUrl(origin, next) },
      });
      if (error) throw error;
    },
    requestPasswordReset: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: authCallbackUrl(origin, '/reset-password'),
      });
      if (error) throw error;
    },
    updatePassword: async (password: string) => {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    },
    signOut: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
  };
}

export function AuthProvider({ providers, children }: { providers: OAuthProvider[]; children: ReactNode }) {
  const supabase = useSupabase();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setIsLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setIsLoading(false);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [supabase]);

  const value = useMemo<AuthContextValue>(
    () => ({ session, user: session?.user ?? null, isLoading, providers, ...buildActions(supabase) }),
    [session, isLoading, providers, supabase],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <PlatformProvider>');
  return ctx;
}
